import axios from 'axios';
import Configuration from '@/models/Configuration';
import RatePlanPrice from '@/models/RatePlanPrice';
import MealPlan from '@/models/MealPlan'
import HotelSearch from '@/models/hotels/HotelSearch';
import Fee from '@/models/Fee';
import Checkout from '@/models/user/Checkout';
import CheckoutExtra from '@/models/CheckoutExtra';

import Booking from '@/models/user/Booking'

import Step from '@/models/app/Step'
import config from '@/utils/config'

import ExternalProduct from '@/models/ExternalProduct';

import AppState from '@/utils/app.state'
import DateSearch from '@/models/DateSearch';
import PaymentMethods from "../../models/PaymentMethods";
import AlternateHotel from "@/models/hotels/AlternateHotel";
import { getCode } from "@/lang";
import { searchPropertyAvailability } from '@/data/service/availabilities';

/**
 * Returns a mapping function which assigns a specified type
 * @param {string} type Type to assign
 * @returns {<T> (item: T) => T & { type: string }}
 */
const assignType = type => item => Object.assign(item, { type })

/**
 * Copies the fields of target object
 * @param {Object} object From where to copy the fields
 * @param {Array<String>} fieldsArray Which fields to take
 * @returns {Object}
 */
function copyFields(object, fieldsArray) {
  return Object.fromEntries(fieldsArray.map(field => [field, object[field]]))
}

/**
 * Calculates the total price of the rateplan
 * @param {{totalPrices: Array<{totalPrice: Number}>}} rateplan The rateplan
 *  for which to calculate the total
 * @returns {Number}
 */
function getTotalRateplanPrice(rateplan) {
  return rateplan.totalPrices.reduce((sum, price) => sum + price.totalPrice, 0)
}

const defaultState = () => ({
  loading: {
    primary: 0,
    secondary: 0,
  },
  loaded: false,
  searching: false,
  alternateHotelsSearching: false,
  preloading: true,
  formValid: false,
  isSameDate: true,
  isDirty: false,
  isBookerAGuest: true,
  propertyId: null,
  addressChanged: false,
  initPaymentToken: null
})

export default {
  namespaced: true,

  state: {
    currency: 'CHF',

    ...defaultState()
  },

  getters: {
    searching(state) {
      return state.searching;
    },
    alternateHotelsSearching(state) {
      return state.alternateHotelsSearching;
    },
    loading(state) {
      return state.loading;
    },
    loaded(state) {
      return state.loaded;
    },
    preloading(state) {
      return state.preloading;
    },
    isBookerAGuest(state) {
      return state.isBookerAGuest
    },
    isFormValid(state) {
      return state.formValid;
    },
    isDirty(state) {
      return state.isDirty;
    },
    getCurrency(state) {
      return state.currency;
    },
    getPropertyId(state) {
      return state.propertyId;
    },

    getAddressChanged(state) {
      return state.addressChanged;
    }
  },

  mutations: {
    setIsBookerAGuest(state, value) {
      state.isBookerAGuest = value
    },

    setInitPaymentToken(state, data) {
      state.initPaymentToken = data;
    },

    setDefaultState(state) {
      Object.assign(state, defaultState())
    },

    setLoading(state, {type, val}) {
      state.loading[type] = val;
    },

    setPreloading(state, data) {
      state.preloading = data;
    },

    setLoaded(state, data) {
      state.loaded = data;
    },

    setSearching(state, data) {
      state.searching = data;
    },

    setAlternateHotelsSearching(state, data) {
      state.alternateHotelsSearching = data;
    },

    setFormValid(state, data) {
      state.formValid = data;
    },

    setDirtyState(state, data) {
      state.isDirty = data;
    },

    setCurrency(state, data) {
      state.currency = data;
    },
    setPropertyId(state, data) {
      state.propertyId = parseInt(data);
    },

    setAddressChanged(state, data) {
      state.addressChanged = data;
    }
  },

  actions: {
    async reset({dispatch, commit}, hotelSearch) {
      commit('setDefaultState')
      commit('setDirtyState', true);
      commit('setLoaded', false);
      await dispatch('entities/deleteAll', null, {root:true});

      if(hotelSearch)
      await HotelSearch.insertOrUpdate({
        data: {
          ...hotelSearch,
          id:1
        }
      });
    },

    async initialStep() {
      // Reset steps to 1st.
      Step.query().get().forEach((step) => {
        Step.insertOrUpdate({
          data: {
            ...step,
            active: step.id === 1,
            enabled: step.id === 1,
            completed: step.id === 1
          }
        });
      });
    },

    /**
     * Triggers the search request
     * @param {*} param0
     * @param {{ guestsByAccommodations: import('@/composables/guests/types').GuestArrangement[][] }} param1
     */
    async findCheapest({ commit, dispatch }, { guestsByAccommodations }) {
      console.log('find cheapest')
      dispatch('resetPayment');

      const dateSearch = DateSearch.query().first();

      if (dateSearch) {
        await HotelSearch.update({
          where: 1,
          data: {
            startDate: dateSearch.dateFrom,
            endDate: dateSearch.dateTo,
          }
        });
      }

      // Get and check hotel search
      const hotelSearch = HotelSearch.instance
      if (
        !hotelSearch ||
        !hotelSearch.startDate ||
        !hotelSearch.endDate ||
        !AppState.currentProperty
      ) return

      commit('setSearching', true)
      dispatch('initialStep')

      // Map occupancies to room configurations
      const roomConfigurations = guestsByAccommodations.map((accommodation, index) => ({
        identifier: index + 1,
        paxAges: accommodation.map(guest => guest.age)
      }))

      Checkout.deleteAll();
      CheckoutExtra.deleteAll();
      ExternalProduct.update({
        where: (record) => record.active,
        data: {
          active: false
        }
      })

      // Add room configurations
      Checkout.insert({
        data: roomConfigurations.map(rc => ({
          id: rc.identifier,
          hotelSearchId: 1,
          configurationId: rc.identifier,
          active: rc.identifier === 1,
          roomSelectedId: null,
          ratePlanSelectedId: null,
          mealPlanSelectedId: null
        }))
      })

      // PropertyID
      let propertyId = AppState.currentProperty

      /* Request data */
      const requestData = {
        checkin: hotelSearch.startDate,
        checkout: hotelSearch.endDate,
        roomConfigurations,
        promoCode: hotelSearch.promotionCode,
        currency: 'CHF',
        showPromoCodeRatePlansOnly: !!hotelSearch.promotionCode
      }

      /* Check for requested room/rateplan */
      const requestedRoomRatePlan = AppState.requestedRoomRatePlan
      if (requestedRoomRatePlan && requestedRoomRatePlan.roomid) {
        requestData.roomIDs = [requestedRoomRatePlan.roomid]
      }
      if (requestedRoomRatePlan && requestedRoomRatePlan.rateid) {
        requestData.ratePlanIDs = [requestedRoomRatePlan.rateid]
      }

      /* check if session has been passed in url */
      let sessionToken = AppState.sessionToken
      if (sessionToken) {
        requestData.session = sessionToken
      }

      const { data } = await axios.post(
        `/v1/accommodation/search/${propertyId}`,
        requestData,
        {
          baseURL: config.searchAndBookApi,
          params: {
            showdailyprices: true,
            showrpprices: true,
            showpkgprices: true,
            showspoprices: true,
            showboardprices: true
          }
        }
      )

      // get alternateHotels availability and minprice when no availability
      AlternateHotel.deleteAll()
      if (!(data.roomConfigurations && data.roomConfigurations.length) && data.alternateHotelSessions) {
        const alternateHotels = data.alternateHotelSessions
        dispatch('getAlternateHotels', { hotelSearch, roomConfigurations, alternateHotels })
      }

      const session = data.session

      Booking.deleteAll()
      Configuration.deleteAll()
      RatePlanPrice.deleteAll()
      PaymentMethods.deleteAll()

      PaymentMethods.api().fetch(hotelSearch.startDate)

      /* Map of meal plans. <code -> object> */
      const mealPlansMap = new Map(
        MealPlan.query().get().map(mealPlan => [mealPlan.code, mealPlan])
      )

      if (data.feeDescriptions) {
        Object.keys(data.feeDescriptions).forEach((fee) => {
          const description = data.feeDescriptions[fee]

          Fee.insertOrUpdate({
            data: {
              id: fee,
              description
            }
          })
          //
          // BookingFee.insertOrUpdate({
          //   bookingId: 1,
          //   feeId: fee
          // })
        })
      }

      /* Parse room configurations */
      for (const roomConfiguration of data.roomConfigurations) {
        const {
          identifier,
          rateplanPrices,
          packagePrices,
          specialOfferPrices
        } = roomConfiguration

        Configuration.insert({
          data: {
            id: identifier,
            bookingId: 1,
            identifier,
            session
          }
        })

        /* Construct rateplans data object */
        const rateplansData = rateplanPrices
          .map(assignType('ratePlanPrice'))
          .concat(packagePrices.map(assignType('packagePlanPrice')))
          .concat(specialOfferPrices.map(assignType('specialPlanPrice')))
          .map(rpItem => ({
            /* Configuration ID */
            configurationId: identifier,

            /* Response fields */
            ...copyFields(rpItem, [
              'excludedTaxBreakdown',
              'includedExtrasIDs',
              'isAvailableOnRequest',
              'pricingType',
              'quantity',
              'ratePlanID',
              'roomID',
              'applicableCancellationPolicyID',
              'applicablePaymentPolicyID',
              'spo',
              'taxBreakdown',
              'totalPrices',
              'type'
            ]),

            /* Derived fields */
            ageGroupIds: rpItem.totalPrices.map(price => price.ageGroupID),

            boardPrices: Object.entries(rpItem.boardPrices).map(([code, value]) => ({
              boardName: code,
              mealPlan: mealPlansMap.get(code),
              ...value[0],

              // fix price calculation w.r.t. to multiple age groups (IBEV4-603)
              // complete solution requires remodelling meal plans to consider age groups
              netPrice: value.reduce((sum, it) => sum + it.netPrice, 0),
              taxAmount: value.reduce((sum, it) => sum + it.taxAmount, 0),
              totalPrice: value.reduce((sum, it) => sum + it.totalPrice, 0)
            })),

            dailyPrices: Object.entries(rpItem.dailyPrices).map(([date, value]) => ({
              date,
              ageGroupPrices: [value[0]]
            })),

            // IBEV4-746: sort by price ascending
            _totalPrice: rpItem.totalPrices.reduce(
              (sum, ageGroupPrice) => sum +
                ageGroupPrice.netPrice +
                ageGroupPrice.taxAmount +
                ageGroupPrice.specialOfferAmount,
              0
            )
          }))
          .sort((a, b) => a._totalPrice - b._totalPrice)

        /* Insert data to ORM */
        RatePlanPrice.insert({ data: rateplansData })
      }

      commit('setLoaded', true);
      commit('setSearching', false);

      commit('setDirtyState', false);
    },

    initiateSearch({ commit, dispatch }, payload) {
      commit('setSearching', true)

      dispatch('findCheapest', payload).finally(() => commit('setSearching', false))
    },

    changeDirtyState({commit}, payload) {
      commit('setDirtyState', payload);
    },

    resetPayment() {
      const paymentMethods = PaymentMethods.query().where(item => item.id !== 3).get();
      paymentMethods.forEach((payment) => {
        PaymentMethods.insertOrUpdate({
          data: {
            ...payment,
            active: false,
            totalAmount: 0,
            status: null,
            transactionId: 0
          }
        });
      });
    },

    addressChanged({commit}, payload) {
      commit('setAddressChanged', payload);
    },

    async getAlternateHotels({ commit }, { hotelSearch, roomConfigurations, alternateHotels }) {
      /* Show loading */
      commit('setAlternateHotelsSearching', true)

      /* Language code */
      const languagecode = getCode()

      /* Axios instance for requests */
      const http = axios.create({ baseURL: config.apiUrl })

      /* Axios instance for APIM CMS API */
      const httpAPIM = axios.create({
        baseURL: 'https://busyrooms.azure-api.net'
      })

      /* Trigger search request */
      const searchRes = await http.post(
        `/search/v2/properties`,
        {
          checkin: hotelSearch.startDate,
          checkout: hotelSearch.endDate,
          roomConfigurations,
          promoCode: hotelSearch.promotionCode,
          currency: 'CHF',
          propertyIDs: Object.keys(alternateHotels)
        },
        {
          params: {
            showdailyprices: false,
            showrpprices: true,
            showpkgprices: true,
            showspoprices: true,
            showboardprices: false
          }
        }
      )

      /* Array of promises */
      const promises = []

      /* Iterate over results */
      for (const property of searchRes.data) {
        if (!property.roomConfigurations.length) continue

        /* Get iterated property id */
        const propertyId = property.propertyID

        /* Merge all rate plans */
        const rateplansMerged = property.roomConfigurations.flatMap(
          roomConfig => (roomConfig.rateplanPrices || [])
            .concat(roomConfig.packagePrices || [])
            .concat(roomConfig.specialOfferPrices || [])
        )

        /* Check if no rateplans are present */
        if (!rateplansMerged.length) { continue }

        /* Find the cheapest rate plan */
        // Todo bug or feature: for multi-room booking the logic only takes 1 rateplan
        const rateplanPrices = rateplansMerged.map(getTotalRateplanPrice)
        const cheapestRateplanIndex = rateplanPrices.reduce(
          (cheapestIndex, currentPrice, currentIndex) => (
            rateplanPrices[cheapestIndex] > currentPrice ? currentIndex : cheapestIndex
          ),
          0
        )
        const cheapestRateplan = rateplansMerged[cheapestRateplanIndex]

        /* Property requests */
        const propertyRequests = [
          /* Get hotel's basic data (hotelname, teaser-text, etc.) */
          http.get(`/accommodation/v1/properties/${propertyId}/information`),

          /* Get property amenities */
          httpAPIM.get(`/reka/v1/ibe/hotel/${propertyId}/amenities`),

          /* Get cheapest rate plan */
          http.get(
            `/accommodation/v1/rateplans/${cheapestRateplan.ratePlanID}`,
            { params: { languagecode, includemultimedia: false }}
          )
        ]

        /* Create a promise for the property (to allow `await`s for the list) */
        const propertyPromise = new Promise(async (resolve) => {
          /* Wait for the completion */
          const responses = await Promise.all(propertyRequests)

          /* Extract responses */
          const basicData = responses[0].data
          const amenitiesData = responses[1].data
          const ratePlanData = responses[2].data

          /* Save into the model */
          await AlternateHotel.insertOrUpdate({
            data: {
              id: propertyId,
              propertyId: propertyId,
              min_price: rateplanPrices[cheapestRateplanIndex],
              property_name: basicData.name,
              descriptions: basicData.shortDescription,
              property_currency: basicData.propertyCurrency,
              image: basicData.images && basicData.images.length && basicData.images[0].url,
              checkin: hotelSearch.startDate,
              checkout: hotelSearch.endDate,
              roomConfigurations,
              promoCode: hotelSearch.promotionCode,
              city: basicData.city,
              country: basicData.country,
              amenities: amenitiesData.data,
              rateName: ratePlanData.descriptions && ratePlanData.descriptions.length
                ? ratePlanData.descriptions[0].friendlyName
                : ratePlanData.name
            }
          })

          /* Resolve the promise */
          resolve(true)
        })

        /* Add promise to the waiting list */
        promises.push(propertyPromise)
      }

      /* Wait for completion */
      await Promise.all(promises)

      /* Hide loading */
      commit('setAlternateHotelsSearching', false)
    }
  }
}
